import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ConfirmedIcon,
  LocalShipping as OutForDeliveryIcon,
  CheckCircleOutline as DeliveredIcon,
  Cancel as CancelledIcon,
  AssignmentReturn as ReturnedIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { getOrderPayableTotal } from '../../utils/orderAmount';
import OrderModal from '../../components/common/OrderModal';
import StatusReasonDialog from '../../components/common/StatusReasonDialog';
import { cacheEndpoints } from '../../utils/apiCache';


const DeliveryOrders = () => {
  const navigate = useNavigate();
  const { actions } = useApp();

  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [timeSlotFilter, setTimeSlotFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [stores, setStores] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    out_for_delivery: 0,
    delivered: 0,
    completed: 0,
    cancelled: 0,
    returned: 0
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  // State for status updates
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  // State for status reason dialog
  const [statusReasonDialog, setStatusReasonDialog] = useState({
    open: false,
    orderId: null,
    newStatus: null,
    loading: false
  });


  useEffect(() => {
    fetchStores();
    fetchTimeSlots();
    fetchStats();
  }, []);

  useEffect(() => {
    // Apply current filters when page changes (but only for pagination, not automatic filtering)
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
  }, [page, rowsPerPage]);

  const fetchStores = async () => {
    try {
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await api.get('/time-slots');
      if (response.data.success) {
        setTimeSlots(response.data.timeSlots || []);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
    }
  };

  const fetchOrders = async (searchParams = {}) => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchParams.search !== undefined ? searchParams.search : searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        branch_id: branchFilter !== 'all' ? branchFilter : undefined,
        timeSlotId: timeSlotFilter !== 'all' ? timeSlotFilter : undefined,
        from_date: searchParams.from_date !== undefined ? searchParams.from_date : fromDate,
        to_date: searchParams.to_date !== undefined ? searchParams.to_date : toDate,
        deliveryFrom: 'PILOT' // Only show PILOT orders
      };

      const response = await api.get('/orders', { params });
      if (response.data.success) {
        // Orders are already in PILOT structure from server
        setOrders(response.data.orders || []);
        setTotalOrders(response.data.total || 0);
        setError(''); // Clear any previous errors on success
      } else {
        setError('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(error.response?.data?.message || 'Error fetching orders');
      actions.showSnackbar('Error fetching orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (searchParams = {}) => {
    try {
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        branch_id: branchFilter !== 'all' ? branchFilter : undefined,
        timeSlotId: timeSlotFilter !== 'all' ? timeSlotFilter : undefined,
        from_date: searchParams.from_date !== undefined ? searchParams.from_date : fromDate,
        to_date: searchParams.to_date !== undefined ? searchParams.to_date : toDate,
        deliveryFrom: 'PILOT' // Only show PILOT orders stats
      };

      const response = await api.get('/orders/stats', { params });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    setError(''); // Clear error when initiating search
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
    fetchStats(searchParams);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleBranchFilterChange = (event) => {
    setBranchFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleTimeSlotFilterChange = (event) => {
    setTimeSlotFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      if (response.data.success) {
        // Order is already in PILOT structure from server
        setSelectedOrder(response.data.order);
        setOrderModalOpen(true);
      } else {
        actions.showSnackbar('Order not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      actions.showSnackbar('Error fetching order details', 'error');
    }
  };

  const handleCloseOrderModal = () => {
    setOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderUpdated = () => {
    // Refresh orders list and stats when an order is updated
    fetchOrders();
    fetchStats();
  };

  const handleRefresh = () => {
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
    fetchStats(searchParams);
  };

  // Status update function
  const handleStatusChange = (orderId, newStatus) => {
    if (!newStatus) return;

    // Check if status requires reason
    if (newStatus === 'cancelled' || newStatus === 'returned') {
      setStatusReasonDialog({
        open: true,
        orderId,
        newStatus,
        loading: false
      });
    } else {
      // Direct status update for other statuses
      handleStatusSave(orderId, newStatus);
    }
  };

  const handleStatusSave = async (orderId, newStatus, statusReason = null) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
      
      const orderData = { status: newStatus };
      if (statusReason) {
        orderData.statusReason = statusReason;
      }
      
      const response = await api.put(`/orders/${orderId}`, { orderData });
      
      if (response.data.success) {
        actions.showSnackbar('Order status updated successfully', 'success');
        
        // Update the order in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            getOrderId(order) === orderId 
              ? { ...order, order_status: newStatus, status: newStatus, statusReason }
              : order
          )
        );
        
        // Refresh stats to reflect the change
        const searchParams = {
          search: searchTerm,
          from_date: fromDate,
          to_date: toDate
        };
        fetchStats(searchParams);
      } else {
        actions.showSnackbar('Failed to update order status', 'error');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      actions.showSnackbar('Error updating order status', 'error');
    } finally {
      setUpdatingStatus(prev => {
        const newUpdating = { ...prev };
        delete newUpdating[orderId];
        return newUpdating;
      });
    }
  };

  const handleStatusReasonConfirm = async (reason) => {
    setStatusReasonDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await handleStatusSave(statusReasonDialog.orderId, statusReasonDialog.newStatus, reason);
      setStatusReasonDialog({ open: false, orderId: null, newStatus: null, loading: false });
    } catch (error) {
      setStatusReasonDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleStatusReasonCancel = () => {
    setStatusReasonDialog({ open: false, orderId: null, newStatus: null, loading: false });
  };


  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getOrderStatus = (order) => {
    return order.order_status || 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'info';
      case 'confirmed': return 'warning';
      case 'processing': return 'secondary';
      case 'out_for_delivery': return 'primary';
      case 'delivered': return 'success';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'returned': return 'error';
      default: return 'default';
    }
  };

  const getOrderAmount = (order) => getOrderPayableTotal(order);

  const getOrderDate = (order) => {
    return order.timeStamp || order.createdAt;
  };

  const getOrderId = (order) => {
    return order.orderId || order.id || 'N/A';
  };

  const getCustomerAddress = (order) => {
    const address = order.deliveryAddress;
    if (!address) return 'N/A';
    
    const parts = [];
    if (address.houseNumber) parts.push(address.houseNumber);
    if (address.street) parts.push(address.street);
    if (address.area) parts.push(address.area);
    if (address.city) parts.push(address.city);
    if (address.pincode) parts.push(address.pincode);
    
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const getCustomerMobile = (order) => {
    return order.userId || 'N/A';
  };

  const getOrderStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'out_for_delivery':
      case 'processing':
      case 'confirmed':
        return 'warning';
      case 'returned':
        return 'warning'; // Orange color for returned orders
      case 'cancelled':
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentMethod = (order) => {
    const paymentMethod = order.payment_method || order.paymentMethod || '';
    
    // Transform payment method display
    const method = paymentMethod.toLowerCase();
    
    // Cash on Delivery variations
    if (method.includes('cash_on_delivery') || method.includes('cod') || method === 'cash') {
      return 'COD';
    }
    
    // Online payment variations
    if (method.includes('pay_by_wallet') || method.includes('online') || method.includes('card') || method.includes('upi')) {
      return 'Online';
    }
    
    // Default fallback
    return paymentMethod?.replace(/_/g, ' ') || 'N/A';
  };

  const getStoreName = (order) => {
    return order.storeDetails?.storeName || 'N/A';
  };

  const getDeliveryDate = (order) => {
    const deliveryDate = order.deliveryDate;
    if (!deliveryDate) return 'N/A';
    
    // Format delivery date without time
    let date;
    if (typeof deliveryDate === 'number') {
      date = new Date(deliveryDate);
    } else if (typeof deliveryDate === 'string') {
      date = new Date(deliveryDate);
    } else if (deliveryDate?.toDate) {
      date = deliveryDate.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeSlotDisplay = (order) => {
    if (order.timeSlotDetails?.displayTime) {
      return order.timeSlotDetails.displayTime;
    }
    
    if (order.timeSlotDetails?.startTime && order.timeSlotDetails?.endTime) {
      return `${order.timeSlotDetails.startTime} - ${order.timeSlotDetails.endTime}`;
    }
    
    return 'N/A';
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const statsCards = [
    {
      title: 'Pending',
      value: stats.pending || 0,
      icon: <PendingIcon sx={{ fontSize: 40 }} />,
      color: 'info.main'
    },
    {
      title: 'Confirmed',
      value: stats.confirmed || 0,
      icon: <ConfirmedIcon sx={{ fontSize: 40 }} />,
      color: 'warning.main'
    },
    {
      title: 'Out for Delivery',
      value: stats.out_for_delivery || 0,
      icon: <OutForDeliveryIcon sx={{ fontSize: 40 }} />,
      color: 'secondary.main'
    },
    {
      title: 'Delivered',
      value: stats.delivered || 0,
      icon: <DeliveredIcon sx={{ fontSize: 40 }} />,
      color: 'success.main'
    },
    {
      title: 'Cancelled',
      value: stats.cancelled || 0,
      icon: <CancelledIcon sx={{ fontSize: 40 }} />,
      color: 'error.main'
    },
    {
      title: 'Returned',
      value: stats.returned || 0,
      icon: <ReturnedIcon sx={{ fontSize: 40 }} />,
      color: 'warning.main'
    }
  ];

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Delivery Orders Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and view all delivery orders in the system
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }} >
        {statsCards.map((stat, index) => (
          <Grid item key={index} sx={{ width: '30%' }}>
            <Card >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex',  flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', textAlign: 'center' }}>
                  <Box sx={{ color: stat.color, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {React.cloneElement(stat.icon, { sx: { fontSize: 25, mr: 1 } })}
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 16, fontWeight: 'bold' }}>
                      {stat.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', ml: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {stat.value}
                    </Typography>
                   
                  </Box>
                  
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* First Row: Filters (50%) and Search (50%) */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Left Side: Store and Status Filters (50%) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '50%' }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Store</InputLabel>
                <Select
                  value={branchFilter}
                  onChange={handleBranchFilterChange}
                  label="Store"
                >
                  <MenuItem value="all">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.branchId} value={store.branchId}>
                      {store.storeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Right Side: Search (50%) */}
            <Box sx={{ width: '50%' }}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Second Row: Date Filters and Time Slot (50%) and Search Button (50%) */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Left Side: Date Filters and Time Slot (50%) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '50%' }}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Time Slot</InputLabel>
                <Select
                  value={timeSlotFilter}
                  onChange={handleTimeSlotFilterChange}
                  label="Time Slot"
                >
                  <MenuItem value="all">All Time Slots</MenuItem>
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot.id} value={slot.id}>
                      {slot.startTime && slot.endTime ? `${slot.startTime} - ${slot.endTime}` : slot.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Right Side: Search Button (50%) */}
            <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ 
                  minWidth: 150,
                  px: 3,
                  height: '40px'
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Delivery Person</TableCell>
                <TableCell>Store Name</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Time Slot</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Method</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={getOrderId(order)} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{getOrderId(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.deliveryPersonName || 'Not Assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getStoreName(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getDeliveryDate(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getTimeSlotDisplay(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getOrderStatus(order)} 
                        color={getStatusColor(getOrderStatus(order))} 
                        size="small" 
                        sx={{ minWidth: 70, textTransform: 'capitalize' }} 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {getPaymentMethod(order)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Order Modal */}
      <OrderModal 
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
      />

      {/* Status Reason Dialog */}
      <StatusReasonDialog
        open={statusReasonDialog.open}
        onClose={handleStatusReasonCancel}
        onConfirm={handleStatusReasonConfirm}
        status={statusReasonDialog.newStatus}
        orderID={statusReasonDialog.orderId}
        loading={statusReasonDialog.loading}
      />
    </Box>
  );
};

export default DeliveryOrders;
